// @auth-coverage:public — intentionally public; see @security-memory
import { createServerFn } from "@tanstack/react-start";
import { sendLeadEmails } from "./email.functions";
import { leadSchema } from "./leads-schema";


export const submitLead = createServerFn({ method: "POST" })
  .validator((input: unknown) => leadSchema.parse(input))
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");

    // Silent honeypot drop — pretend success so bots don't retry.
    if (data.website && data.website.trim().length > 0) {
      return { success: true };
    }

    const url = process.env.SUPABASE_URL;
    const key = process.env.SUPABASE_SERVICE_ROLE_KEY;

    if (!url || !key) {
      throw new Error("Server configuration error");
    }

    const supabase = createClient(url, key, {
      auth: { persistSession: false, autoRefreshToken: false },
    });

    const { data: inserted, error } = await supabase.from("leads").insert({
      type: data.type,
      service: data.service || null,
      name: data.name,
      phone: data.phone,
      email: data.email || null,
      vehicle_reg: data.vehicle_reg || null,
      vehicle_make_model: data.vehicle_make_model || null,
      vehicle_year: data.vehicle_year || null,
      notes: data.preferred_contact
        ? `${data.notes ? data.notes + "\n" : ""}Preferred contact: ${data.preferred_contact.toUpperCase()}`
        : data.notes || null,
      urgency: data.urgency || null,
      preferred_date: data.preferred_date || null,
      preferred_time: data.preferred_time || null,
      message: data.message || null,
      callback_time: data.callback_time || null,
      area: data.area || null,
      source_page: data.source_page || null,
      utm: data.utm && Object.keys(data.utm).length > 0 ? data.utm : null,
      email_status: "pending",
    }).select("id").single();

    if (error || !inserted) {
      console.error("Lead insert error:", error);
      throw new Error("Failed to save your request. Please try calling us instead.");
    }

    // Best-effort email send — never throws upward. If Resend fails, the lead
    // is already saved with email_status=failed and a log entry is appended.
    try {
      await sendLeadEmails(inserted.id);
    } catch (err) {
      console.error("sendLeadEmails failed (lead still saved):", err);
      try {
        await supabase
          .from("leads")
          .update({
            email_status: "failed",
            email_error: err instanceof Error ? err.message : "Unknown email error",
          })
          .eq("id", inserted.id);
      } catch {/* swallow */}
    }

    // Optional SMS confirmation — only if the customer explicitly opted in.
    // Fails silently: SMS is a courtesy, never blocks lead intake.
    let smsResult: { sent: boolean; reason?: string; sid?: string } = { sent: false, reason: "not_requested" };
    if (data.sms_opt_in && data.phone) {
      try {
        const { sendBookingConfirmationSms } = await import("./sms.server");
        smsResult = await sendBookingConfirmationSms({
          toPhone: data.phone,
          name: data.name,
          service: data.service,
          preferred_date: data.preferred_date,
          preferred_time: data.preferred_time,
        });
      } catch (err) {
        console.error("sendBookingConfirmationSms threw (lead still saved):", err);
        smsResult = { sent: false, reason: "sms_exception" };
      }
      try {
        await supabase
          .from("leads")
          .update({
            notes: (
              (data.notes ? data.notes + "\n" : "") +
              `SMS opt-in: yes — ${smsResult.sent ? `sent (sid ${smsResult.sid})` : `not sent (${smsResult.reason})`}`
            ).slice(0, 2000),
          })
          .eq("id", inserted.id);
      } catch {/* swallow */}
    }

    // Short customer-facing reference derived from the lead UUID.
    // Matches the 8-char hex convention used elsewhere in the app so full
    // UUIDs never leak into UI, email subjects, or SMS.
    const reference = String(inserted.id).replace(/-/g, "").slice(0, 8).toUpperCase();
    return { success: true, reference, sms: smsResult };
  });
